import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStockTransferById, completeStockTransfer, cancelStockTransfer } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-600",
  completed: "bg-green-50 text-green-600",
  cancelled: "bg-red-50 text-red-500",
};

// A StockTransfer is never edited once created (see models.StockTransfer's
// own doc comment) — this page only ever displays it and, while still
// Pending, offers exactly the two actions that end its lifecycle: Complete
// Transfer (writes the actual StockMovement/StockLevel changes at both
// branches) or Cancel (abandons it with no stock effect at all).
export default function ViewStockTransfer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("stock_transfers");
  const { t: tCommon } = useTranslation("common");

  const [transfer, setTransfer] = useState(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [status, setStatus] = useState("idle");

  const load = () => {
    getStockTransferById(token, id).then(setTransfer).catch(() => notify.error(t("notify_load_failed", "Failed to load stock transfer")));
  };

  useEffect(() => { load(); }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleComplete = async () => {
    try {
      setStatus("loading");
      await completeStockTransfer(token, id);
      setStatus("success");
      setTimeout(() => { setCompleteModalOpen(false); setStatus("idle"); }, 1200);
      notify.success(t("notify_completed", "Stock transfer completed — stock has been moved"));
      load();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_complete_failed", "Failed to complete stock transfer"));
    }
  };

  const handleCancel = async () => {
    try {
      setStatus("loading");
      await cancelStockTransfer(token, id);
      setStatus("success");
      setTimeout(() => { setCancelModalOpen(false); setStatus("idle"); }, 1200);
      notify.success(t("notify_cancelled", "Stock transfer cancelled"));
      load();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_cancel_failed", "Failed to cancel stock transfer"));
    }
  };

  if (!transfer) return null;

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <ConfirmModal
        isOpen={completeModalOpen}
        title={t("complete_confirm_title", "Complete Stock Transfer")}
        message={t("complete_confirm_message", "This moves every line item's quantity from the source branch to the destination branch and can't be undone. Continue?")}
        onConfirm={handleComplete}
        onCancel={() => setCompleteModalOpen(false)}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
        confirmLabel={t("btn_complete", "Complete Transfer")}
        confirmVariant="primary"
        successTitle={t("complete_success_title", "Completed!")}
        successMessage={t("notify_completed", "Stock transfer completed — stock has been moved")}
      />
      <ConfirmModal
        isOpen={cancelModalOpen}
        title={t("cancel_confirm_title", "Cancel Stock Transfer")}
        message={t("cancel_confirm_message", "Are you sure? No stock has moved yet — this transfer will be marked cancelled.")}
        onConfirm={handleCancel}
        onCancel={() => setCancelModalOpen(false)}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
        confirmLabel={t("btn_cancel_transfer", "Cancel Transfer")}
        successTitle={t("cancel_success_title", "Cancelled!")}
        successMessage={t("notify_cancelled", "Stock transfer cancelled")}
      />

      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/stock-transfers")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div className="flex-grow">
          <h2 className="text-2xl font-black text-gray-800 tracking-tight font-mono">{transfer.code}</h2>
          <span className={`inline-block mt-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLES[transfer.status] || "bg-gray-100 text-gray-500"}`}>
            {t(`status_${transfer.status}`, transfer.status)}
          </span>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_from_branch_label", "From Branch")}</p>
            <p className="font-bold text-gray-800">{transfer.from_branch?.name || `#${transfer.from_branch_id}`}</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_to_branch_label", "To Branch")}</p>
            <p className="font-bold text-gray-800">{transfer.to_branch?.name || `#${transfer.to_branch_id}`}</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_created_by_label", "Created By")}</p>
            <p className="font-bold text-gray-800">{transfer.created_by?.username || `#${transfer.created_by_id}`}</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_created_at_label", "Created At")}</p>
            <p className="font-bold text-gray-800">{new Date(transfer.created_at).toLocaleString()}</p>
          </div>
        </div>

        {transfer.notes && (
          <div>
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_notes_label", "Notes")}</p>
            <p className="text-gray-700 text-sm">{transfer.notes}</p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3">{t("items_section_title", "Line Items")}</h3>
          <div className="space-y-2">
            {(transfer.items || []).map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex-grow">
                  <p className="font-bold text-gray-800 text-sm">{item.product_name}</p>
                  <p className="text-xs text-gray-400 font-mono">{item.product_sku}</p>
                </div>
                <div className="text-xs text-gray-500 font-mono">{item.quantity}</div>
              </div>
            ))}
          </div>
        </div>

        {transfer.status === "completed" && transfer.completed_by && (
          <p className="text-xs text-gray-400 italic">
            {t("detail_completed_line", "Completed by {user} on {date}")
              .replace("{user}", transfer.completed_by.username)
              .replace("{date}", new Date(transfer.completed_at).toLocaleString())}
          </p>
        )}
        {transfer.status === "cancelled" && transfer.cancelled_by && (
          <p className="text-xs text-gray-400 italic">
            {t("detail_cancelled_line", "Cancelled by {user} on {date}")
              .replace("{user}", transfer.cancelled_by.username)
              .replace("{date}", new Date(transfer.cancelled_at).toLocaleString())}
          </p>
        )}

        {transfer.status === "pending" && (
          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button variant="primary" icon={ICONS.StockTransfers} onClick={() => setCompleteModalOpen(true)} className="flex-grow shadow-lg shadow-blue-500/10">
              {t("btn_complete", "Complete Transfer")}
            </Button>
            <Button variant="danger" onClick={() => setCancelModalOpen(true)}>{t("btn_cancel_transfer", "Cancel Transfer")}</Button>
          </div>
        )}
        {transfer.status !== "pending" && (
          <div className="pt-6 border-t border-gray-50">
            <Button variant="secondary" onClick={() => navigate("/stock-transfers")}>{tCommon("btn_back", "Back")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
